import React, {useState} from 'react';
import {
  Tabs,
  Tab,
  Button,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import {Rnd} from 'react-rnd';
import DeleteIcon from '@mui/icons-material/Delete';

interface CanvasItem {
  id: number;
  type: 'heading' | 'paragraph' | 'field';
  label: string;
  text: string;
  fontSize: number;
  fontFamily: string;
  color: string;
  position: {x: number; y: number};
  size: {width: number; height: number};
}

function TemplateEditor() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [canvasItems, setCanvasItems] = useState<CanvasItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<CanvasItem | null>(null);

  const elements = [
    {label: 'Heading', type: 'heading'},
    {label: 'Paragraph', type: 'paragraph'}
  ];

  const fields = [
    {label: 'User Name'},
    {label: 'Course Name'},
    {label: 'Completion Date'}
  ];

  const addCanvasItem = (
    type: 'heading' | 'paragraph' | 'field',
    label: string
  ) => {
    const newItem = {
      id: Date.now(),
      type,
      label,
      text: label,
      fontSize: type === 'heading' ? 24 : 16,
      fontFamily: 'Arial',
      color: '#000',
      position: {x: 100, y: 100},
      size: {width: 150, height: 50}
    };
    setCanvasItems([...canvasItems, newItem]);
    setSelectedItem(newItem);
  };

  const updateSelectedItem = (key: keyof CanvasItem, value: any) => {
    if (selectedItem) {
      setCanvasItems(items =>
        items.map(item =>
          item.id === selectedItem.id ? {...item, [key]: value} : item
        )
      );
      setSelectedItem({...selectedItem, [key]: value});
    }
  };

  const deleteSelectedItem = () => {
    if (selectedItem) {
      setCanvasItems(items =>
        items.filter(item => item.id !== selectedItem.id)
      );
      setSelectedItem(null);
    }
  };

  return (
    <Box display="flex">
      <Box width={240} bgcolor="#2E3B55" color="white" p={2}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}>
          <Tab label="Elements" />
          <Tab label="Fields" />
        </Tabs>

        <Box mt={2}>
          {selectedTab === 0 &&
            elements.map(element => (
              <Button
                key={element.label}
                fullWidth
                variant="outlined"
                sx={{color: '#ddd', mb: 1}}
                onClick={() => {
                  if (
                    ['heading', 'paragraph', 'field'].includes(element.type)
                  ) {
                    addCanvasItem(
                      element.type as 'heading' | 'paragraph' | 'field',
                      element.label
                    );
                  }
                }}>
                {element.label}
              </Button>
            ))}

          {selectedTab === 1 &&
            fields.map(field => (
              <Button
                key={field.label}
                fullWidth
                variant="outlined"
                sx={{color: '#ddd', mb: 1}}
                onClick={() => addCanvasItem('field', field.label)}>
                {field.label}
              </Button>
            ))}
        </Box>
      </Box>

      <Box flexGrow={1} position="relative" p={3} bgcolor="#f0f0f0">
        {canvasItems.map(item => (
          <Rnd
            key={item.id}
            position={item.position}
            size={{width: item.size.width, height: item.size.height}}
            onDragStop={(e, data) => {
              updateSelectedItem('position', {x: data.x, y: data.y});
            }}
            onResizeStop={(e, direction, ref, delta, position) => {
              updateSelectedItem('size', {
                width: parseInt(ref.style.width),
                height: parseInt(ref.style.height)
              });
            }}
            onClick={() => setSelectedItem(item)}
            bounds="parent">
            <Typography
              style={{
                fontSize: `${item.fontSize} ! important`,
                fontFamily: item.fontFamily,
                color: item.color,
                width: '100%',
                height: '100%',
                border:
                  selectedItem?.id === item.id ? '1px solid blue' : 'none',
                padding: '4px',
                cursor: 'pointer',
                overflow: 'hidden'
              }}>
              {item.text}
            </Typography>
          </Rnd>
        ))}
      </Box>

      {selectedItem && (
        <Box p={2} width={300} bgcolor="#f7f7f7" ml={2}>
          <Typography variant="h6">
            Edit {selectedItem.label}
            <IconButton onClick={deleteSelectedItem} color="error">
              <DeleteIcon />
            </IconButton>
          </Typography>
          <TextField
            label="Text"
            fullWidth
            value={selectedItem.text}
            onChange={e => updateSelectedItem('text', e.target.value)}
          />
          <TextField
            label="Font Size"
            type="number"
            fullWidth
            value={selectedItem.fontSize}
            onChange={e =>
              updateSelectedItem('fontSize', Number(e.target.value))
            }
          />
          <TextField
            label="Width"
            type="number"
            fullWidth
            value={selectedItem.size.width}
            onChange={e =>
              updateSelectedItem('size', {
                ...selectedItem.size,
                width: Number(e.target.value)
              })
            }
          />
          <TextField
            label="Height"
            type="number"
            fullWidth
            value={selectedItem.size.height}
            onChange={e =>
              updateSelectedItem('size', {
                ...selectedItem.size,
                height: Number(e.target.value)
              })
            }
          />
          <Select
            label="Font Family"
            fullWidth
            value={selectedItem.fontFamily}
            onChange={e => updateSelectedItem('fontFamily', e.target.value)}>
            <MenuItem value="Arial">Arial</MenuItem>
            <MenuItem value="Courier New">Courier New</MenuItem>
            <MenuItem value="Georgia">Georgia</MenuItem>
          </Select>
          <TextField
            label="Color"
            type="color"
            fullWidth
            value={selectedItem.color}
            onChange={e => updateSelectedItem('color', e.target.value)}
          />
        </Box>
      )}
    </Box>
  );
}

export default TemplateEditor;
